import { useEffect, useState, useCallback } from "react";
import {
  PublicKey,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";
import { getConnection } from "@/lib/mint";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Wallet, Loader2, ExternalLink, Crown } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import TipGoal from "@/components/TipGoal";
import TipperThanks from "@/components/TipperThanks";

const PRESETS = [0.01, 0.05, 0.1, 0.5];
const SHORT = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");
const fmtSol = (n) => (Number(n) || 0).toFixed(4);

/**
 * Tipping panel for a coin's /c/:slug page.
 * Lets visitors send SOL straight to creator_wallet via Phantom, then
 * verifies the transaction server-side via Helius and credits the tip.
 *
 * Props: { coin: { slug, name, ticker, creator_wallet } }
 */
export default function TipCoin({ coin }) {
  const { phantom, connectPhantom } = useWallet();
  const [amount, setAmount] = useState(0.05);
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState(null);
  const [board, setBoard] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/${coin.slug}/tips`);
      setBoard(data);
    } catch (e) {
      console.warn("tips leaderboard reload failed", e?.message);
    }
  }, [coin.slug]);
  useEffect(() => {
    reload();
  }, [reload]);

  const recipient = board?.creator_wallet || coin.creator_wallet;

  const tip = async () => {
    if (!recipient) {
      toast.error("Creator hasn't linked a wallet yet");
      return;
    }
    if (!phantom.address) {
      toast.info("Connect Phantom to tip", { description: "We'll open the picker." });
      await connectPhantom();
      return;
    }
    if (amount <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    setBusy(true);
    try {
      // 1) intent — server returns a reference key it'll match on verify
      const { data: intent } = await api.post(`/coins/${coin.slug}/tip/intent`, {
        amount_sol: Number(amount),
      });

      // 2) build + sign + send the tx (tips are mainnet SOL)
      const conn = getConnection("mainnet");
      const payer = new PublicKey(phantom.address);
      const dest = new PublicKey(recipient);
      const tx = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: payer,
          toPubkey: dest,
          lamports: intent.creator_lamports ?? intent.amount_lamports,
        })
      );
      if (intent.fee_lamports > 0 && intent.treasury) {
        tx.add(
          SystemProgram.transfer({
            fromPubkey: payer,
            toPubkey: new PublicKey(intent.treasury),
            lamports: intent.fee_lamports,
          })
        );
      }
      const { blockhash } = await conn.getLatestBlockhash("confirmed");
      tx.recentBlockhash = blockhash;
      tx.feePayer = payer;

      // Phantom: signAndSendTransaction returns {signature}
      const signed = await window.solana.signAndSendTransaction(tx);
      const signature = signed?.signature;
      if (!signature) throw new Error("no signature returned");

      toast.success("Tip sent — confirming on-chain…", { description: SHORT(signature) });

      // 3) wait for confirmation, then verify on our backend
      await conn.confirmTransaction(signature, "confirmed");
      const { data } = await api.post(`/coins/${coin.slug}/tip/verify`, {
        signature,
        reference: intent.reference,
      });
      if (data.already) {
        toast.success("Tip already credited");
      } else {
        toast.success(`Tip credited — ${fmtSol(data.amount_sol)} SOL`);
        setThanks({ sol: data.amount_sol });
      }
      reload();
    } catch (e) {
      const msg = e?.message?.includes("User rejected")
        ? "Tip cancelled"
        : e.response?.data?.detail || e.message || "Tip failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const linkMyWallet = async () => {
    if (!phantom.address) {
      await connectPhantom();
      return;
    }
    setLinkBusy(true);
    try {
      await api.patch(`/coins/${coin.slug}/link-wallet`, {
        wallet_address: phantom.address,
      });
      toast.success("Wallet linked", { description: SHORT(phantom.address) });
      reload();
    } catch (e) {
      const msg = e.response?.status === 409
        ? "A wallet is already linked to this coin."
        : e.response?.data?.detail || "Couldn't link wallet";
      toast.error(msg);
    } finally {
      setLinkBusy(false);
    }
  };

  return (
    <section
      className="border border-[#3a3a3a] bg-[#121212] p-6 mb-6"
      data-testid="tip-section"
    >
      {thanks && <TipperThanks coin={coin} sol={thanks.sol} onClose={() => setThanks(null)} />}
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-3">
        // Tip in SOL · real mainnet
      </div>

      {!recipient ? (
        <div className="grid grid-cols-12 gap-4 items-center" data-testid="tip-unlinked">
          <div className="col-span-12 md:col-span-8">
            <div className="font-display text-2xl font-bold uppercase mb-1">No tip wallet yet.</div>
            <p className="text-sm text-[#e2e2e2]">
              If this is your coin, link your Solana wallet and the world can tip you in SOL. One-time, one-click.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <button
              onClick={linkMyWallet}
              disabled={linkBusy}
              data-testid="tip-link-wallet"
              className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3 inline-flex items-center gap-2"
            >
              {linkBusy ? <Loader2 size={12} className="animate-spin" /> : <Wallet size={12} />}
              {phantom.address ? "Link my Phantom" : "Connect & link"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6" data-testid="tip-linked">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-display text-2xl font-bold uppercase mb-1">
              Tip {coin.name} <span className="text-[#cfff04]">${coin.ticker}</span>
            </div>
            <p className="text-sm text-[#e2e2e2] mb-4">
              Tips go straight to{" "}
              <a
                href={`https://solscan.io/account/${recipient}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[#cfff04] hover:underline"
                data-testid="tip-recipient-link"
              >
                {SHORT(recipient)} <ExternalLink size={11} className="inline -mt-0.5" />
              </a>
              . Sent on-chain via Solana Pay, verified per transaction.
            </p>

            <TipGoal coin={coin} totalSol={board?.total_sol || 0} />

            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS.map((v) => (
                <button
                  key={v}
                  onClick={() => setAmount(v)}
                  data-testid={`tip-preset-${v}`}
                  className={`border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                    Number(amount) === v
                      ? "border-[#cfff04] text-[#cfff04]"
                      : "border-[#3a3a3a] text-[#e2e2e2] hover:border-[#cfff04] hover:text-[#cfff04]"
                  }`}
                >
                  {v} SOL
                </button>
              ))}
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  step="0.01"
                  min="0.001"
                  max="100"
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="tip-custom"
                  className="w-32 bg-[#0c0c0c] border border-[#3a3a3a] focus:border-[#cfff04] focus:outline-none px-3 py-2 font-mono text-sm pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
                  SOL
                </span>
              </div>
            </div>

            <button
              onClick={tip}
              disabled={busy}
              data-testid="tip-send"
              className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-7 py-4 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} fill="currentColor" />}
              {phantom.address ? `Send ${fmtSol(amount)} SOL` : "Connect & Tip"}
            </button>
            <p className="font-mono text-[11px] text-[#d0d0d0] uppercase tracking-widest mt-3">
              // signed via Phantom · verified by Helius RPC · stored once
            </p>
          </div>

          <aside className="col-span-12 lg:col-span-5">
            <div className="border border-[#3a3a3a] bg-[#050505] p-5">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
                Tips received
              </div>
              <div className="font-display text-3xl font-black mb-1" data-testid="tip-total">
                {fmtSol(board?.total_sol)} SOL
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] mb-5">
                {board?.tip_count || 0} tip{(board?.tip_count || 0) === 1 ? "" : "s"}
              </div>

              {(board?.top_tippers?.length ? board.top_tippers : board?.top || []).length === 0 ? (
                <div className="text-[#d0d0d0] text-sm">No tips yet — be the first.</div>
              ) : (
                <ol className="space-y-2" data-testid="tip-leaderboard">
                  {(board?.top_tippers?.length ? board.top_tippers : board.top).slice(0, 8).map((t, i) => {
                    const RANK_COLORS = ["#ffd700", "#C0C0C0", "#CD7F32"];
                    const rankColor = RANK_COLORS[i] || null;
                    const total = t.total_lamports ?? t.amount_lamports;
                    return (
                      <li
                        key={t.payer_pubkey + i}
                        data-testid={`tip-rank-${i + 1}`}
                        className={`flex items-center justify-between gap-3 font-mono text-xs ${
                          i === 0 ? "border border-[#ffd700]/40 bg-[#ffd700]/5 px-2 py-1.5 -mx-2" : ""
                        }`}
                      >
                        <span className="flex items-center gap-2 text-[#e2e2e2] min-w-0">
                          {rankColor ? (
                            <Crown size={12} style={{ color: rankColor }} className="flex-shrink-0" />
                          ) : (
                            <span className="text-[#d0d0d0] w-3 text-center">{i + 1}</span>
                          )}
                          <span className="truncate">{SHORT(t.payer_pubkey)}</span>
                          {i === 0 && (
                            <span className="text-[10px] uppercase tracking-widest text-[#ffd700] flex-shrink-0">
                              top whale
                            </span>
                          )}
                          {t.count > 1 && (
                            <span className="text-[10px] text-[#d0d0d0] flex-shrink-0">×{t.count}</span>
                          )}
                        </span>
                        <span className="text-[#cfff04] flex-shrink-0">
                          {fmtSol(total / 1_000_000_000)} SOL
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
